/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useRingGroupForm.ts (Ring Groups)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Ring Group form state, validation, and submission.
    Handles create, update, and duplicate operations.

────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createRingGroup, updateRingGroup } from '../api/ringGroupsApi';
import { FormState, MemberRow } from '../types/ringGroups.types';
import { getDefaultFormState } from '../utils/formDefaults';
import { validateForm } from '../utils/validators';
import { normalizeMemberRows } from '../utils/memberHelpers';
import { buildSettingsFromFormState } from '../utils/settingsHelpers';

/**
 * Hook for managing Ring Group form
 */
export function useRingGroupForm(
  id: string | undefined,
  duplicateId: string | null,
  initialMappedData: { formState: FormState; members: MemberRow[]; extraSettings: Record<string, unknown> } | null
) {
  const location = useLocation();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();

  const isEditMode = Boolean(id && id !== 'new' && !duplicateId);
  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [originalFormState, setOriginalFormState] = useState<FormState>(defaultFormState);
  const [memberRows, setMemberRows] = useState<MemberRow[]>([]);
  const [originalMemberRows, setOriginalMemberRows] = useState<MemberRow[]>([]);
  const [extraSettings, setExtraSettings] = useState<Record<string, unknown>>({});
  const [originalExtraSettings, setOriginalExtraSettings] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const initializedRef = useRef(false);
  const lastInitialDataRef = useRef<string | null>(null);
  const lastIdRef = useRef<string | undefined>(undefined);
  const lastLocationKeyRef = useRef<string>('');

  // Reset form when id changes
  useEffect(() => {
    if (lastIdRef.current !== id) {
      lastIdRef.current = id;
      initializedRef.current = false;
      lastInitialDataRef.current = null;
    }
  }, [id]);

  // Initialize form data when initialMappedData changes
  useEffect(() => {
    const locationKey = location.key || location.pathname + location.search;
    const locationChanged = lastLocationKeyRef.current !== locationKey;
    
    if (initialMappedData) {
      const dataStr = JSON.stringify(initialMappedData);
      const lastDataStr = lastInitialDataRef.current;

      // Always update if:
      // 1. Data changed (different JSON string)
      // 2. Not initialized yet
      // 3. Location changed (navigation occurred, e.g., "Save and New")
      //    This is critical for "Save and New" when creating new items
      //    We check locationChanged BEFORE updating lastLocationKeyRef to detect the change
      if (dataStr !== lastDataStr || !initializedRef.current || locationChanged) {
        lastInitialDataRef.current = dataStr;
        lastLocationKeyRef.current = locationKey;
        setFormState({ ...initialMappedData.formState });
        setOriginalFormState(duplicateId ? getDefaultFormState() : { ...initialMappedData.formState });
        setMemberRows([...initialMappedData.members]);
        setOriginalMemberRows(duplicateId ? [] : initialMappedData.members.map(m => ({ ...m })));
        setExtraSettings({ ...initialMappedData.extraSettings });
        setOriginalExtraSettings(duplicateId ? {} : { ...initialMappedData.extraSettings });
        setErrors({});
        initializedRef.current = true;
      }
    } else if (!isEditMode && !duplicateId) {
      // Reset refs when navigating to new form to ensure clean state
      // If location changed, force reset even if already initialized
      if (locationChanged) {
        initializedRef.current = false;
        lastInitialDataRef.current = null;
      }
      
      // New form - initialize with defaults
      if (!initializedRef.current) {
        const defaultState = getDefaultFormState();
        setFormState({ ...defaultState });
        setOriginalFormState({ ...defaultState });
        setMemberRows([]);
        setOriginalMemberRows([]);
        setExtraSettings({});
        setOriginalExtraSettings({});
        setErrors({});
        initializedRef.current = true;
        lastLocationKeyRef.current = locationKey;
      }
    }
    // Edit mode but no data yet - wait for data to load
    // Don't reset here, let the data loading hook handle it
  }, [initialMappedData, isEditMode, duplicateId, id, location.key, location.pathname, location.search]);

  const handleFieldChange = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleMembersChange = useCallback((members: MemberRow[]) => {
    setMemberRows(members);
  }, []);

  const handleExtraSettingsChange = useCallback((settings: Record<string, unknown>) => {
    setExtraSettings(settings);
  }, []);

  const hasChanges = useMemo(() => {
    const formChanged = Object.keys(formState).some((key) => {
      const formKey = key as keyof FormState;
      return formState[formKey] !== originalFormState[formKey];
    });

    const membersChanged = memberRows.length !== originalMemberRows.length ||
      memberRows.some((member, idx) => {
        const original = originalMemberRows[idx];
        if (!original) return true;
        return member.extension !== original.extension ||
          member.delay !== original.delay ||
          member.timeout !== original.timeout ||
          member.prompt !== original.prompt ||
          member.description !== original.description ||
          member.enabled !== original.enabled ||
          member.orderIndex !== original.orderIndex;
      });

    const settingsChanged = JSON.stringify(extraSettings) !== JSON.stringify(originalExtraSettings);

    return formChanged || membersChanged || settingsChanged;
  }, [formState, memberRows, extraSettings, originalFormState, originalMemberRows, originalExtraSettings]);

  const handleSave = useCallback(async () => {
      // hasChanges validation removed

    const validationErrors = validateForm(formState, memberRows, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const errorMsgs = Object.values(validationErrors).filter(msg => typeof msg === 'string').join(' • ');
      showError(`${t('ringGroups.messages.validationError')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      throw new Error('Validation failed');
    }

    setIsSaving(true);
    try {
      const normalizedMembers = normalizeMemberRows(
        memberRows,
        Number(formState.ringTimeout) || 30
      );

      if (normalizedMembers.length === 0) {
        showError(t('ringGroups.messages.membersRequired'));
        setIsSaving(false);
        throw new Error('Members required');
      }

      const settings = buildSettingsFromFormState(formState);
      const mergedSettings = { ...settings, ...extraSettings };

      const fallbackModule = formState.fallbackModule.trim() || 'none';
      const fallbackDestination =
        fallbackModule === 'none' ? null : formState.fallbackValue.trim() || null;

      const payload = {
        name: formState.name.trim(),
        description: formState.description.trim() || null,
        context: formState.context.trim() || 'default',
        strategy: formState.strategy,
        ringTimeout: Number(formState.ringTimeout) || 30,
        skipBusy: formState.skipBusy,
        fallbackModule,
        fallbackDestination,
        enabled: formState.enabled,
        settings: mergedSettings,
        members: normalizedMembers
      };

      // Validate id exists before updating
      if (isEditMode) {
        if (!id || id === 'new') {
          showError(t('ringGroups.messages.invalidId') || 'Invalid ring group ID');
          setIsSaving(false);
          throw new Error('Invalid ID');
        }
        
        const result = await updateRingGroup(id, payload);
        const successMessage = t('ringGroups.messages.updateSuccess');
        showSuccess(successMessage);
        
        // Update original state
        setOriginalFormState({ ...formState });
        setOriginalMemberRows(memberRows.map(m => ({ ...m })));
        setOriginalExtraSettings({ ...extraSettings });
        setIsSaving(false);
        
        // Return saved data (navigation is now handled by useSaveWithAction)
        return { id, uuid: result?.uuid || id, ...result };
      }

      // Create new ring group
      const result = await createRingGroup(payload);
      const successMessage = t('ringGroups.messages.createSuccess');
      showSuccess(successMessage);

      // Update original state
      setOriginalFormState({ ...formState });
      setOriginalMemberRows(memberRows.map(m => ({ ...m })));
      setOriginalExtraSettings({ ...extraSettings });
      setIsSaving(false);

      // Return saved data (navigation is now handled by useSaveWithAction)
      return result;
    } catch (error) {
      setIsSaving(false);
      const message = error instanceof Error ? error.message : t('ringGroups.messages.saveError');
      showError(message);
      throw error;
    }
  }, [formState, memberRows, extraSettings, hasChanges, id, isEditMode, showError, showInfo, showSuccess, t]);

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormState({ ...originalFormState });
      setMemberRows(originalMemberRows.map(m => ({ ...m })));
      setExtraSettings({ ...originalExtraSettings });
      setErrors({});
      return;
    }

    const defaultState = getDefaultFormState();
    setFormState(defaultState);
    setOriginalFormState({ ...defaultState });
    setMemberRows([]);
    setOriginalMemberRows([]);
    setExtraSettings({});
    setOriginalExtraSettings({});
    setErrors({});
  }, [isEditMode, originalFormState, originalMemberRows, originalExtraSettings]);

  return {
    formState,
    memberRows,
    extraSettings,
    errors,
    isSaving,
    hasChanges,
    handleFieldChange,
    handleMembersChange,
    handleExtraSettingsChange,
    handleSave,
    handleCancel
  };
}

